import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Col,
    Row,
    Spin,
    Image,
    Tabs,
    TabsProps,
    Form,
    Space,
    Select,
} from "antd";
import { CloseOutlined, DownOutlined, LoadingOutlined } from "@ant-design/icons";
import PracticesService, {
    ClientReviewerUserOptionsType,
    IGeneralSettingsInitialValues,
    IPractice,
    PracticeTabsType,
    PracticeTabsTypeList,
    PracticeUpdateTypes,
} from "../../Services/PracticesService";
import "./Practices.css";
import { adminContext } from "../../Common/PageRoute";
import { PageHeader } from "../../Common/MainLayout";
import NoImageToShow from "../../images/no_Image_to_show.png";
import BasicInformation from "./BasicInformation";
import TimeZoneVATAndEmail from "./TimeZoneVATAndEmail";
import PracticeSequence from "./PracticeSequence";
import Features from "./Features";
import GeneralSettings from "./GeneralSettings";
import { PracticeBankAccount } from "./PracticeBankAccount";
import AMLReport from "./AMLReport";
import SubmissionHoldList from "../SubmissionHold/SubmissionHoldList";
import BiometricStats from "./BiometricStats";
import { useUser } from "../../Context/UserContext";
import { usePractice } from "../../Context/PracticeContext";

const defaultData = {
    id: "",
    address: {
        building: "",
        city: "",
        country: "",
        county: "",
        postcode: "",
        street: "",
    },
    params: {
        dueInvoicesTaskDays: 0,
        pageTrackTimeout: 0,
        taskDueDateInDays: 0,
        secureLinkExpiry: 0,
    },
    creditNoteReviewAmount: 0,
    dueDateConfig: {
        number: 0,
        period: 0,
    },
    clientReviewerPracticeProfile: {
        userOption: ClientReviewerUserOptionsType.Unknown,
    },
    appFeature: {
        accounts: false,
        aml: false,
        bankStatement: false,
        bookkeeping: false,
        calls: false,
        chats: false,
        clientDataRequest: false,
        corporationTax: false,
        dayEnd: false,
        leadSources: false,
        linkedinLeads: false,
        payroll: false,
        teams: false,
        webForms: false,
        timeSheet: false,
        careOf: false,
        selfAssessment: false,
        dms: false,
        audit: false,
        companiesHouseSync: false,
        other1: false,
        other2: false,
        other3: false,
    },
    appSubFeature: {
        accountManagar: false,
        bankDetails: false,
        actionTab: false,
        addCustomer: false,
        digitalSignatureWithoutAML: false,
        manualTimesheet: false,
        progressiveLeads: false,
        websiteQueriesWithOTP: false,
        withoutDiscount: false,
        clientAutomation: false,
        proofOfEarningFPS: false,
        receiptAttachments: false,
        skipTeamUpdate: false,
        secureLink: false,
        amLforAllOfficers: false,
        firstResponse: false,
        accountant: false,
        mode: false,
        agentCredentialsRemark: false,
        clientBrand: false,
        s455TaxLiability: false,
        scheduleEmail: false,
        resourceSection: false,
        allowTLandAdminToUpdateTaskDeadline: false,
        directorAccountWhileRetrieve: false,
        reviewNotificationToTeamleader: false,
        noChangeInTaskAssigneeWithTeamChange: false,
        loeFinancialAutomation: false,
        collectionMethod: false,
        chargeDay: false,
        clientsJoinersAndLeavers: false,
        blockInvoiceEdit: false,
        masking: false,
        xcelify: false,
        clientReferenceInInvoice: false,
        payeMasterReport: false,
        accaBotChat: false,
        preparationSideValidation: false,
        cardNet: false,
        teamSalesComparison: false,
        creditNoteReviewRequest: false,
        reviewAutoRefund: false,
        biometric: false,
        reviewRequestAttachment: false,
        accaBot: false,
        autoComplete: false,
        emailSummarizer: false,
        bookOcrText: false,
        callTranscription: false,
        teamGroup: false,
        teamIsolation: false,
        mtsAutomationSettings: {
            automate: false,
            days: 0,
        },
        noReply: {
            enabled: false,
            replyEmail: "",
        },
        reviewRequestConfig: {
            APCT: false,
            SA100: false,
            SA800: false,
            VAT: false,
        },
    },
    bankAccount: {
        accountName: "",
        accountNumber: "",
        bankName: "",
        bik: "",
        iban: "",
        shortCode: "",
    },
    website: {
        iconUrl: "",
        logoUrl: "",
        url: "",
        urlPrivacy: "",
        urlTnC: "",
    },
    name: "",
    email: "",
    phoneNumber: 0,
    timeZone: "",
    vat: {
        enabled: false,
        rate: 0,
    },
    emailProvider: 0,
    defaultBCCs: [],
    defaultCCs: [],
    sequences: {
        invoice: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        quote: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        creditNote: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        receipt: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
        refund: {
            leadingZero: 0,
            postfix: "",
            prefix: "",
            seperator: "",
            type: 0,
        },
    },
    badges: {
        warningMessage: "",
        dateFrom: "",
        dateTo: "",
        dateRange: [],
        sA100: false,
        sA800: false,
        cis: false,
        vat: false,
        eps: false,
        fps: false,
        confirmationStatement: false,
        corporationTax: false,
        accountsReport: false,
    },
};

const PracticeDetails = ({ data }: any) => {
    const { openNotification } = React.useContext(adminContext);
    const { isSubAdmin } = useUser();
    const isViewOnly = isSubAdmin();
    const [form] = Form.useForm();
    const { id } = useParams<{ id: string }>();
    const practiceId = Number(id);
    const navigate = useNavigate();
    const { allPracticeIds } = usePractice();

    const [loading, setLoading] = React.useState(false);
    const [practicesData, setPracticesData] = React.useState<IPractice>(defaultData);

    // Search select state
    const [searchInput, setSearchInput] = React.useState(false);
    const selectRef = React.useRef<any>(null);

    useEffect(() => {
        if (searchInput && selectRef.current) {
            selectRef.current.focus();
        }
    }, [searchInput]);

    const [practiceGS, setPracticeGS] = React.useState<IGeneralSettingsInitialValues>({
        name: "",
        email: "",
        logoUrl: "",
        connectionString: "",
        database: "",
        host: "",
        identifier: "",
    });
    const [activeTab, setActiveTab] = React.useState<string>(PracticeTabsType.generalSettings);
    const [isEdit, setIsEdit] = React.useState<{ type: PracticeUpdateTypes }>({
        type: PracticeUpdateTypes.unknown,
    });
    const [refreshBasicInfo, setRefreshBasicInfo] = React.useState(0);

    useEffect(() => {
        setActiveTab(PracticeTabsType.generalSettings);
        setPracticesData(defaultData);
        form.resetFields();
    }, [practiceId]);  // eslint-disable-line react-hooks/exhaustive-deps

    const fetchGeneralSettings = async () => {
        try {
            setLoading(true);
            const res = await PracticesService.getPracticeGeneralSettings(practiceId);
            if (res.status && res?.result) {
                setPracticeGS(res?.result);
            }
        } catch (error) {
            openNotification("error", "Failed to fetch practice details.");
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === PracticeTabsType.generalSettings) {
            fetchGeneralSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, practiceId]);

    React.useEffect(() => {
        if (practicesData && Object.keys(practicesData).length > 0) {
            form.setFieldsValue(practicesData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practicesData]);

    const handleValuesChange = async (changedValues: any, allValues: any) => {
        setPracticesData(allValues);
    };

    const currentPracticeLabel =
        allPracticeIds.find((o) => o.value === practiceId)?.label ||
        `Practice - ${practiceId}`;

    const items: TabsProps["items"] = [
        {
            key: PracticeTabsType.generalSettings,
            label: "General settings",
            children: (
                <>
                    <GeneralSettings
                        isEdit={isEdit}
                        setIsEdit={setIsEdit}
                        setLoading={setLoading}
                        generalSettingsData={practiceGS}
                        practiceId={practiceId}
                        setPracticeGS={setPracticeGS}
                    />
                </>
            ),
        },
        {
            key: PracticeTabsType.basicInformation,
            label: "Basic information",
            children: (
                <>
                    <BasicInformation
                        isEdit={isEdit}
                        setIsEdit={setIsEdit}
                        setLoading={setLoading}
                        practiceId={practiceId}
                        refreshTrigger={refreshBasicInfo}          
                    />
                </>
            ),
        },
        {
            key: PracticeTabsType.bankAccountDetails,
            label: "Bank accounts",
            children: (
                <PracticeBankAccount
                     key={practiceId}
                    practiceId={practiceId}
                    onPrimaryChange={() => setRefreshBasicInfo((prev) => prev + 1)}
                />
            ),
        },
        {
            key: PracticeTabsType.timeZoneVatEmail,
            label: "Time zone, VAT & Email",
            children: (
                <TimeZoneVATAndEmail
                    isEdit={isEdit}
                    setIsEdit={setIsEdit}
                    setLoading={setLoading}
                    practiceId={practiceId}
                />
            ),
        },
        {
            key: PracticeTabsType.sequenceAndReview,
            label: "Sequence & Review",
            children: (
                <>
                    <PracticeSequence
                        isEdit={isEdit}
                        setIsEdit={setIsEdit}
                        setLoading={setLoading}
                        practiceId={practiceId}
                    />

                </>
            ),
        },
        {
            key: PracticeTabsType.features,
            label: "Features",
            children: (
                <Features
                    isEdit={isEdit}
                    setIsEdit={setIsEdit}
                    setLoading={setLoading}
                    practiceId={practiceId}
                />
            ),
        },
        ...(!isViewOnly ? [{
            key: PracticeTabsType.submissionHold,
            label: "Submission Hold",
            children: (
                <SubmissionHoldList />
            ),
        }] : []),
        {
            key: PracticeTabsType.aml,
            label: "AML",
            children: (
                <AMLReport
                    practiceId={practiceId}
                />
            ),
        },
        {
            key: PracticeTabsType.biometric,
            label: "AML-Biometric",
            children: (
                <BiometricStats
                    practiceId={practiceId}
                />
            ),
        },
    ];

    return (
        <Spin
            spinning={loading}
            indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />}
        >
            <Form
                form={form}
                initialValues={practicesData}
                onValuesChange={handleValuesChange}
            >
                <Row justify="start" align="middle" style={{ margin: "1px 15px" }}>
                    <PageHeader
                        item={[
                            {
                                label: "Practices",
                                path: "/practices",
                            },
                            {
                                label: searchInput ? (
                                    <Space>
                                        <Select
                                            ref={selectRef}
                                            size="small"
                                            showSearch
                                            autoFocus
                                            placeholder="Search practice"
                                            defaultValue={practiceId}
                                            onChange={(selectedId) => {
                                                setSearchInput(false);
                                                navigate(`/practices/${selectedId}`);
                                            }}
                                            onBlur={() => setSearchInput(false)}
                                            style={{ width: 260 }}
                                            options={allPracticeIds}
                                            filterOption={(input, option) =>
                                                (option?.label?.toString() ?? "")
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                            }
                                        />
                                        <CloseOutlined
                                            style={{ paddingRight: 0, color: "#395cd2" }}
                                            onClick={() => setSearchInput(false)}
                                        />
                                    </Space>
                                ) : (
                                    <div
                                        style={{
                                            paddingRight: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            cursor: "pointer",
                                        }}
                                        onClick={() => setSearchInput(true)}
                                    >
                                        <span style={{ fontWeight: 600 }}>
                                            {currentPracticeLabel}
                                        </span>
                                        <DownOutlined style={{ fontSize: 11 }} />
                                    </div>
                                ),
                            },
                            {
                                label: PracticeTabsTypeList[Number(activeTab)],
                            },
                        ]}
                    />
                </Row>
                <Row
                    className="roundedCornerSmall p-1"
                    style={{ backgroundColor: "white", padding: "10px 20px" }}
                >
                    <Col span={24} style={{ minHeight: window.outerHeight - 196.6 }}>
                        <Row>
                            <Col
                                span={23}
                                style={{ display: "flex", alignItems: "center", padding: 10 }}
                            >
                                <Image
                                    className="rounded"
                                    style={{ height: "44px", width: "auto" }}
                                    src={
                                        practiceGS?.logoUrl !== undefined &&
                                            practiceGS?.logoUrl !== "" &&
                                            practiceGS?.logoUrl !== null
                                            ? `${practiceGS?.logoUrl}`
                                            : NoImageToShow
                                    }
                                    alt="Company logo"
                                />
                            </Col>
                            <Col span={23}>
                                <Tabs
                                    destroyInactiveTabPane={true}
                                    activeKey={activeTab}
                                    size="small"
                                    items={items}
                                    className="custom-tabs"
                                    onChange={(key) => {
                                        setActiveTab(key);
                                    }}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Form>
        </Spin>
    );
};

export default PracticeDetails;



